/* eslint-disable react-hooks/exhaustive-deps */
import {
  useMediaQuery,
  Grid,
  Box,
  useTheme,
  Typography,
  Button,
  Container,
  Skeleton,
  Divider,
  TextField,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import CustomRightImage from "@components/CustomRightImagev2";
import { useLazyGetTaxSystemConfigQuery } from "@api/applications";
import { useEffect, useState } from "react";
import { ApiError, TaxSystemConfigResponse } from "@interfaces/index";
import { formatCurrency } from "@helpers/funciones";
import icon_check from "@assets/img/icon_check_cotizador.svg";
import useInterfaceConfig from "@hooks/useInterfaceConfig";
import { ButtonTypeSelectedCustom } from "@components/ButtonTypeSelectedCustom";
import { StyledSlider } from "@components/CustomSlider";
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAlt";
import { useAppDispatch } from "@store/store";
import { usePostDocumentQuotationTableMutation } from "@api/documents";
import { setModalErrorVisible } from "@store/slices/appSlice";

interface MarketStructure {
  value: number;
  label: string;
}

const CotizadorArrendaminetoLaniAuto = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const interfaceConfig = useInterfaceConfig();
  const dispatch = useAppDispatch();

  const [getTaxSystemConfig] = useLazyGetTaxSystemConfigQuery();
  const [postGenerateQuotationTable] = usePostDocumentQuotationTableMutation();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [personType, setPersonType] = useState<string>("PM");
  const [productType, setProductType] = useState<string>("arrendamiento");
  const [error, setError] = useState<boolean>(false);
  const [termMarks, setTermMarks] = useState<{ value: number; label: string }[]>([]);
  const [anticipo, setAnticipo] = useState<number>(20);
  const [advancePercentageMarket, setAdvancePercentageMarket] = useState<MarketStructure[]>([]);

  const [cotizadorConfig, setCotizadorConfig] = useState<TaxSystemConfigResponse>({
    id: "",
    code: "",
    product_id: "",
    minAmount: 0,
    maxAmount: 0,
    stepAmount: 0,
    minTerm: 0,
    maxTerm: 0,
    stepTerm: 0,
    defaultAmount: 0,
    defaultTerm: 0,
    tax_system_type: "",
    minResidual: 0,
    maxResidual: 0,
    stepResidual: 0,
    defaultResidual: 0,
    minAdvance: 0,
    maxAdvance: 0,
    stepAdvance: 0,
    defaultAdvance: 0,
  });

  const [selectedTerm, setSelectedTerm] = useState<number>(cotizadorConfig.defaultTerm);
  const [monthlyRate, setMonthlyRate] = useState<number>(0);
  const [selectedAmount, setSelectedAmount] = useState<number>(cotizadorConfig.defaultAmount);
  const [amountDraft, setAmountDraft] = useState<string | null>(null);
  const [amountTouched, setAmountTouched] = useState<boolean>(false);
  const [valorAnticipo, setValorAnticipo] = useState<number>(0);
  const [valorResidual, setValorResidual] = useState<number>(0);

  const formatAmountEditable = (amount: number) => {
    if (!Number.isFinite(amount)) return "";
    const rounded = Math.round(amount * 100) / 100;
    return rounded % 1 === 0 ? String(Math.trunc(rounded)) : String(rounded);
  };

  const normalizeAmount = (amount: number) => {
    const min = cotizadorConfig.minAmount ?? 0;
    const max = cotizadorConfig.maxAmount ?? 0;
    const step = cotizadorConfig.stepAmount ?? 0;

    if (!Number.isFinite(amount)) return cotizadorConfig.defaultAmount ?? 0;

    const bounded = Math.min(Math.max(amount, min), max);
    if (step > 0) {
      const stepped = min + Math.round((bounded - min) / step) * step;
      return Math.min(Math.max(stepped, min), max);
    }

    return bounded;
  };

  const parseAmountDraft = (value: string) => {
    const cleaned = value.replace(/[^0-9.]/g, "");
    if (!cleaned) return NaN;

    const parts = cleaned.split(".");
    const normalized = parts.length <= 2 ? cleaned : `${parts[0]}.${parts.slice(1).join("")}`;
    return Number(normalized);
  };

  const amountRangeErrorText = (() => {
    const min = cotizadorConfig.minAmount ?? 0;
    const max = cotizadorConfig.maxAmount ?? 0;
    if (!amountTouched) return "";
    if (!Number.isFinite(selectedAmount)) return "";
    if (selectedAmount < min || selectedAmount > max) {
      return `El mínimo es ${formatCurrency(min)} y el máximo es ${formatCurrency(max)}`;
    }
    return "";
  })();

  const isAmountOutOfRange = Boolean(amountRangeErrorText);

  const [configPM, setConfigPM] = useState<TaxSystemConfigResponse | null>(null);
  const [configPFAE, setConfigPFAE] = useState<TaxSystemConfigResponse | null>(null);
  const [configPF, setConfigPF] = useState<TaxSystemConfigResponse | null>(null);

  const [configPMAutomotriz, setConfigPMAutomotriz] = useState<TaxSystemConfigResponse | null>(null);
  const [configPFAEAutomotriz, setConfigPFAEAutomotriz] = useState<TaxSystemConfigResponse | null>(null);
  const [configPFAutomotriz, setConfigPFAutomotriz] = useState<TaxSystemConfigResponse | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const [
          responsePM,
          responsePFAE,
          responsePF,
          responsePMAutomotriz,
          responsePFAEAutomotriz,
          responsePFAutomotriz,
        ] = await Promise.all([
          getTaxSystemConfig("CSLR1").unwrap(),
          getTaxSystemConfig("CSLR2").unwrap(),
          getTaxSystemConfig("CSLR3").unwrap(),
          getTaxSystemConfig("CSL01").unwrap(),
          getTaxSystemConfig("CSL02").unwrap(),
          getTaxSystemConfig("CSL03").unwrap(),
        ]);
        setConfigPM(responsePM);
        setConfigPFAE(responsePFAE);
        setConfigPF(responsePF);
        setConfigPMAutomotriz(responsePMAutomotriz);
        setConfigPFAEAutomotriz(responsePFAEAutomotriz);
        setConfigPFAutomotriz(responsePFAutomotriz);
      } catch (error) {
        console.error("Error fetching tax system configs", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const configByProductAndPerson: Record<string, Record<string, TaxSystemConfigResponse | null>> = {
      arrendamiento: {
        PM: configPM,
        PFAE: configPFAE,
        PF: configPF,
      },
      credito_automotriz: {
        PM: configPMAutomotriz,
        PFAE: configPFAEAutomotriz,
        PF: configPFAutomotriz,
      },
    };

    const selectedConfig = configByProductAndPerson[productType]?.[personType];

    if (selectedConfig) {
      setCotizadorConfig(selectedConfig);
    }
  }, [
    personType,
    productType,
    configPM,
    configPFAE,
    configPF,
    configPMAutomotriz,
    configPFAEAutomotriz,
    configPFAutomotriz,
  ]);

  useEffect(() => {
    const { minTerm, maxTerm, stepTerm, defaultAdvance, defaultTerm, defaultAmount } = cotizadorConfig;
    if (minTerm < maxTerm && stepTerm > 0) {
      const marks = [];
      for (let i = minTerm; i <= maxTerm; i += stepTerm) {
        marks.push({ value: i, label: `${i}` });
      }

      const maxAdvance = cotizadorConfig.maxAdvance ?? 0;
      const minAdvance = cotizadorConfig.minAdvance ?? 0;
      const stepAdvance = cotizadorConfig.stepAdvance ?? 0;

      const listAdvance: MarketStructure[] = [];

      if (stepAdvance > 0 && minAdvance > 0 && maxAdvance >= minAdvance) {
        for (let r = minAdvance; r <= maxAdvance; r += stepAdvance) {
          listAdvance.push({ value: r, label: `${r}%` });
        }
        if (listAdvance.length && listAdvance[listAdvance.length - 1].value !== maxAdvance) {
          listAdvance.push({ value: maxAdvance, label: `${maxAdvance}%` });
        }
      }

      setAdvancePercentageMarket(listAdvance);
      setSelectedTerm(defaultTerm);
      setTermMarks(marks);
      setSelectedAmount(defaultAmount);
      setAmountDraft(null);
      setAnticipo(defaultAdvance || 20);
    }
  }, [cotizadorConfig]);

  const setConditions = () => {
    setAmountTouched(true);
    if (isAmountOutOfRange) {
      return;
    }
    if (!cotizadorConfig.product_id || !selectedAmount || !selectedAmount || !monthlyRate || !productType) {
      setError(true);
      return;
    }

    let residual = 0;
    if (productType === "arrendamiento") {
      residual = 20;
    }

    const sourceCode = sessionStorage.getItem("source_code");
    let urlPortal = `/producto/arrendamiento/crear_cuenta?tax_system_id=${cotizadorConfig.id}&product_id=${cotizadorConfig.product_id}&person_type=${cotizadorConfig.code}&loan_amount=${selectedAmount}&loan_term=${selectedTerm}&tax_system_type=${cotizadorConfig.tax_system_type}&advance_percentage=${anticipo}&residual_percentage=${residual}`;
    if (sourceCode) {
      urlPortal += `&source_code=${sourceCode}`;
    }
    navigate(urlPortal);
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sourceCode = params.get("code");

    if (sourceCode) {
      sessionStorage.setItem("source_code", sourceCode);
    } else {
      sessionStorage.removeItem("source_code");
    }
  }, []);

  useEffect(() => {
    if (productType !== "") {
      setError(false);
    }
  }, [productType]);

  useEffect(() => {
    const iva = 0.16;
    const esArrendamiento = productType === "arrendamiento";
    const residual = esArrendamiento ? 20 : 0;

    if (esArrendamiento) {
      // Excel:
      // - El monto del vehículo viene con IVA y se convierte a sin IVA (E8 = F8/1.16)
      // - Anticipo y residual se calculan sobre el valor del vehículo (sin IVA)
      // - El pago base se calcula con PMT(tasa_mensual, plazo, -PV, FV, 0)
      // - El pago mostrado es con IVA (M8 = L8*1.16)
      const tasaAnual = 0.324;
      const tasaMensual = tasaAnual / 12;

      const valorVehiculoConIva = selectedAmount;
      const valorVehiculoSinIva = valorVehiculoConIva / (1 + iva);

      const valorAnticipoSinIva = valorVehiculoSinIva * (anticipo / 100);
      const valorAFinanciarSinIva = valorVehiculoSinIva - valorAnticipoSinIva;
      const valorResidualSinIva = valorVehiculoSinIva * (residual / 100);

      // Para UI (montos con IVA, como el usuario los espera)
      const valorAnticipoConIva = valorVehiculoConIva * (anticipo / 100);
      const valorResidualConIva = valorVehiculoConIva * (residual / 100);

      if (valorAFinanciarSinIva <= 0 || selectedTerm <= 0) {
        setMonthlyRate(0);
        setValorAnticipo(valorAnticipoConIva);
        setValorResidual(valorResidualConIva);
        return;
      }

      const factor = (1 + tasaMensual) ** -selectedTerm;
      const den = 1 - factor;
      const pagoBaseSinIva =
        den === 0 ? 0 : ((valorAFinanciarSinIva - valorResidualSinIva * factor) * tasaMensual) / den;
      const pagoMensualConIva = pagoBaseSinIva * (1 + iva);

      setMonthlyRate(pagoMensualConIva);
      setValorAnticipo(valorAnticipoConIva);
      setValorResidual(valorResidualConIva);
      return;
    }

    // Crédito automotriz (como en Python generate_tabla_amortizacion_arracapital)
    // - pre_finan = monto - anticipo
    // - comisión = pre_finan * 3% * 1.16
    // - principal = pre_finan + comisión
    // - capital fijo = principal / plazo
    // - interés fijo = principal * 2% mensual
    // - IVA = interés * 0.16
    // - pago total fijo = capital + interés + IVA
    const COMMISSION_RATE = 0.03;
    const INTEREST_RATE_MONTHLY = 0.02;

    const valorAnticipoMonto = selectedAmount * (anticipo / 100);
    const preFinan = selectedAmount - valorAnticipoMonto;

    if (preFinan <= 0 || selectedTerm <= 0) {
      setMonthlyRate(0);
      setValorAnticipo(valorAnticipoMonto);
      setValorResidual(0);
      return;
    }

    // Nota: GPS / seguros (si se requieren) deberían sumarse aquí antes de comisión.
    // const gps = preFinan * GPS_RATE;
    // const lifeInsurance = preFinan * LIFE_INSURANCE_RATE;
    // const autoInsurance = preFinan * AUTO_INSURANCE_RATE;
    // const base = preFinan + gps + lifeInsurance + autoInsurance;
    // const commission = base * COMMISSION_RATE * (1 + iva);
    // const principal = base + commission;

    const commission = preFinan * COMMISSION_RATE * (1 + iva);
    const principal = preFinan + commission;

    const pagoCapital = principal / selectedTerm;
    const interes = principal * INTEREST_RATE_MONTHLY;
    const ivaInteres = interes * iva;
    const pagoTotal = pagoCapital + interes + ivaInteres;

    setMonthlyRate(pagoTotal);
    setValorAnticipo(valorAnticipoMonto);
    setValorResidual(0);
  }, [selectedAmount, selectedTerm, anticipo, cotizadorConfig.defaultResidual, productType]);

  const genarateQuotationTable = async () => {
    try {
      setIsLoading(true);

      let quotationResponse: unknown;

      if (productType === "credito_automotriz") {
        quotationResponse = await postGenerateQuotationTable({
          amount: selectedAmount,
          term: selectedTerm,
          company: interfaceConfig?.name || "",
          credit_type: "credito_automotriz",
          advanced_percentage: anticipo,
        }).unwrap();
      } else if (productType === "arrendamiento") {
        quotationResponse = await postGenerateQuotationTable({
          amount: selectedAmount,
          term: selectedTerm,
          company: interfaceConfig?.name || "",
          credit_type: "arrendamiento",
          advanced_percentage: anticipo,
          residual_percentage: 20,
        }).unwrap();
      }

      const base64String = (() => {
        if (typeof quotationResponse === "string") return quotationResponse;
        if (!quotationResponse || typeof quotationResponse !== "object") return null;

        const data = (quotationResponse as { data?: unknown }).data;
        if (typeof data === "string") return data;
        if (data && typeof data === "object") {
          const nested = (data as { data?: unknown }).data;
          if (typeof nested === "string") return nested;
        }

        return null;
      })();

      if (!base64String) {
        throw new Error("No se pudo generar la tabla de cotización");
      }

      const byteCharacters = atob(base64String);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const file = new Blob([byteArray], { type: "application/pdf" });

      const fileURL = URL.createObjectURL(file);

      const link = document.createElement("a");
      link.href = fileURL;
      link.download =
        productType === "arrendamiento"
          ? "tabla_amortizacion_arrendamiento.pdf"
          : "tabla_amortizacion_credito_automotriz.pdf";
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      URL.revokeObjectURL(fileURL);
    } catch (error) {
      const api_error = error as ApiError;
      dispatch(setModalErrorVisible({ open: true, error: api_error }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Grid container height="100%">
        <Grid item xs={12} md={8}>
          <Container maxWidth={"sm"}>
            <Typography fontWeight={500} variant="h3" mt={4} color={theme?.palette?.primary?.dark} textAlign={"center"}>
              Cotizador
            </Typography>

            <Box>
              <Box>
                <Typography
                  fontWeight={500}
                  fontSize={"16px"}
                  mt={1}
                  color={theme?.palette?.primary?.dark}
                  textAlign={"center"}
                >
                  Selecciona tu régimen fiscal
                </Typography>

                <Grid
                  container
                  spacing={isMobileOrTablet ? 1 : 2}
                  mt={1}
                  mr={isMobileOrTablet ? 1 : 0}
                  justifyContent="center"
                >
                  <Grid item xs={4} sm={4} md={4} display="flex" justifyContent="center">
                    {!isLoading ? (
                      <ButtonTypeSelectedCustom
                        title="Persona Física"
                        onClick={() => setPersonType("PF")}
                        isSelected={personType === "PF"}
                        isMovile={isMobileOrTablet}
                        height={"60px"}
                        heightMoviles="60px"
                        width={"170px"}
                        widthMoviles="130px"
                        icon={icon_check}
                        width_icon="22px"
                        height_icon="22px"
                        font_size={"12px"}
                        top_icon="-12px"
                        font_size_movil="11px"
                      />
                    ) : (
                      <Skeleton
                        variant="rectangular"
                        animation="wave"
                        width={isMobileOrTablet ? 150 : 190}
                        height={isMobileOrTablet ? 60 : 70}
                      />
                    )}
                  </Grid>
                  <Grid item xs={4} sm={4} md={4} display="flex" justifyContent="center">
                    {!isLoading ? (
                      <ButtonTypeSelectedCustom
                        title="Persona física con actividad empresarial"
                        onClick={() => setPersonType("PFAE")}
                        isSelected={personType === "PFAE"}
                        isMovile={isMobileOrTablet}
                        height={"60px"}
                        heightMoviles="60px"
                        width={"170px"}
                        widthMoviles="130px"
                        icon={icon_check}
                        width_icon="22px"
                        height_icon="22px"
                        font_size={"12px"}
                        top_icon="-12px"
                        font_size_movil="11px"
                      />
                    ) : (
                      <Skeleton
                        variant="rectangular"
                        animation="wave"
                        width={isMobileOrTablet ? 150 : 190}
                        height={isMobileOrTablet ? 60 : 70}
                      />
                    )}
                  </Grid>
                  <Grid item xs={4} sm={4} md={4} display="flex" justifyContent="center">
                    {!isLoading ? (
                      <ButtonTypeSelectedCustom
                        title="Persona Moral"
                        onClick={() => setPersonType("PM")}
                        isSelected={personType === "PM"}
                        isMovile={isMobileOrTablet}
                        height={"60px"}
                        heightMoviles="60px"
                        width={"170px"}
                        widthMoviles="130px"
                        icon={icon_check}
                        width_icon="22px"
                        height_icon="22px"
                        font_size={"12px"}
                        top_icon="-12px"
                        font_size_movil="11px"
                      />
                    ) : (
                      <Skeleton
                        variant="rectangular"
                        animation="wave"
                        width={isMobileOrTablet ? 150 : 190}
                        height={isMobileOrTablet ? 60 : 70}
                      />
                    )}
                  </Grid>
                </Grid>
              </Box>

              <Box mt={2}>
                <Typography
                  fontWeight={500}
                  fontSize={"16px"}
                  mt={1}
                  color={theme?.palette?.primary?.dark}
                  textAlign={"center"}
                >
                  Selecciona tu tipo de producto
                </Typography>

                <Grid container spacing={isMobileOrTablet ? 0 : 2} mt={1} mr={isMobileOrTablet ? 1 : 0}>
                  <Grid item xs={6} md={6} display="flex" justifyContent={isMobileOrTablet ? "center" : "flex-end"}>
                    {!isLoading ? (
                      <ButtonTypeSelectedCustom
                        title="Arrendamiento Automotriz"
                        onClick={() => {
                          setProductType("arrendamiento");
                        }}
                        isSelected={productType === "arrendamiento"}
                        isMovile={isMobileOrTablet}
                        height={"60px"}
                        heightMoviles="60px"
                        width={"170px"}
                        widthMoviles="130px"
                        icon={icon_check}
                        width_icon="22px"
                        height_icon="22px"
                        font_size={"12px"}
                        top_icon="-12px"
                        font_size_movil="11px"
                      />
                    ) : (
                      <Skeleton
                        variant="rectangular"
                        animation="wave"
                        width={isMobileOrTablet ? 150 : 190}
                        height={isMobileOrTablet ? 60 : 70}
                      />
                    )}
                  </Grid>
                  <Grid item xs={6} md={6} display="flex" justifyContent={isMobileOrTablet ? "center" : "flex-start"}>
                    {!isLoading ? (
                      <ButtonTypeSelectedCustom
                        title=" Crédito Automotriz"
                        onClick={() => {
                          setProductType("credito_automotriz");
                        }}
                        isSelected={productType === "credito_automotriz"}
                        isMovile={isMobileOrTablet}
                        height={"60px"}
                        heightMoviles="60px"
                        width={"170px"}
                        widthMoviles="130px"
                        icon={icon_check}
                        width_icon="22px"
                        height_icon="22px"
                        font_size={"12px"}
                        top_icon="-12px"
                        font_size_movil="11px"
                      />
                    ) : (
                      <Skeleton
                        variant="rectangular"
                        animation="wave"
                        width={isMobileOrTablet ? 150 : 190}
                        height={isMobileOrTablet ? 60 : 70}
                      />
                    )}
                  </Grid>
                </Grid>
                {error && (
                  <>
                    <Typography fontWeight={400} fontSize={"12px"} mt={2} color={"#d32f2f"} textAlign={"center"}>
                      Selecciona un tipo de producto para continuar
                    </Typography>
                  </>
                )}
              </Box>

              <Box mt={4} ml={isMobileOrTablet ? 3 : 1} mr={isMobileOrTablet ? 3 : 1}>
                <Typography
                  fontWeight={500}
                  fontSize={"16px"}
                  color={theme?.palette?.primary?.dark}
                  textAlign={"center"}
                >
                  Ingresa el valor estimado del vehículo
                </Typography>

                {!isLoading ? (
                  <Box mt={2} display="flex" justifyContent="center">
                    <TextField
                      aria-label="amount"
                      id="estimated_value"
                      name="estimated_value"
                      label="Valor estimado del vehículo + IVA"
                      margin="normal"
                      required
                      sx={{
                        maxWidth: 300,
                        "& .MuiInputBase-input": {
                          textAlign: "center",
                        },
                      }}
                      autoComplete="off"
                      fullWidth
                      value={amountDraft !== null ? amountDraft : formatCurrency(selectedAmount || 0)}
                      onFocus={() => {
                        setAmountTouched(true);
                        setAmountDraft(formatAmountEditable(selectedAmount || 0));
                      }}
                      onChange={(event) => {
                        const nextDraft = event.target.value;
                        const parsed = parseAmountDraft(nextDraft);
                        setAmountTouched(true);

                        if (!nextDraft) {
                          setAmountDraft("");
                          setSelectedAmount(0);
                          return;
                        }

                        if (!Number.isFinite(parsed)) {
                          setAmountDraft(nextDraft);
                          return;
                        }

                        setSelectedAmount(parsed);
                        setAmountDraft(nextDraft);
                      }}
                      onBlur={() => {
                        const parsed = parseAmountDraft(amountDraft ?? "");
                        const next = Number.isFinite(parsed) ? parsed : (cotizadorConfig.defaultAmount ?? 0);

                        const min = cotizadorConfig.minAmount ?? 0;
                        const max = cotizadorConfig.maxAmount ?? 0;
                        const step = cotizadorConfig.stepAmount ?? 0;
                        const shouldSnap = next >= min && next <= max && step > 0 && step < 1;
                        const normalized = shouldSnap ? normalizeAmount(next) : next;
                        const rounded = Math.round(normalized * 100) / 100;

                        setSelectedAmount(rounded);
                        setAmountDraft(null);
                      }}
                      inputProps={{ maxLength: 20, inputMode: "numeric" }}
                      error={Boolean(amountRangeErrorText)}
                      helperText={amountRangeErrorText}
                    />
                  </Box>
                ) : (
                  <Skeleton variant="rectangular" animation="wave" />
                )}
              </Box>

              <Box mb={1} ml={isMobileOrTablet ? 3 : 1} mr={isMobileOrTablet ? 3 : 1} mt={2}>
                <Typography
                  fontWeight={500}
                  fontSize={"16px"}
                  mb={2}
                  color={theme?.palette?.primary?.dark}
                  textAlign={"center"}
                >
                  Selecciona el plazo en meses
                </Typography>

                {!isLoading ? (
                  <Box display="flex" justifyContent="center" flexWrap="wrap" gap={1.5}>
                    {termMarks.map((term) => {
                      return (
                        <ButtonTypeSelectedCustom
                          key={term.value}
                          title={`${term.label}`}
                          onClick={() => setSelectedTerm(term.value)}
                          isSelected={selectedTerm === term.value}
                          isMovile={isMobileOrTablet}
                          height="44px"
                          heightMoviles="40px"
                          width={"38px"}
                          widthMoviles={"38px"}
                          font_size={"12px"}
                          font_size_movil={"12px"}
                        />
                      );
                    })}
                  </Box>
                ) : (
                  <Skeleton variant="rectangular" animation="wave" />
                )}
              </Box>

              <Box mb={1} ml={isMobileOrTablet ? 3 : 1} mr={isMobileOrTablet ? 3 : 1}>
                <Typography
                  fontWeight={500}
                  fontSize={"16px"}
                  mb={2}
                  color={theme?.palette?.primary?.dark}
                  textAlign={"center"}
                >
                  Selecciona el porcentaje de anticipo
                </Typography>

                {!isLoading ? (
                  <StyledSlider
                    aria-label="advance"
                    min={cotizadorConfig.minAdvance}
                    max={cotizadorConfig.maxAdvance}
                    step={cotizadorConfig.stepAdvance}
                    defaultValue={cotizadorConfig.defaultAdvance}
                    marks={advancePercentageMarket}
                    value={anticipo}
                    onChange={(_event, newValue) => setAnticipo(newValue as number)}
                    valueLabelDisplay="on"
                  />
                ) : (
                  <Skeleton variant="rectangular" animation="wave" />
                )}
              </Box>

              {productType === "arrendamiento" ? (
                <Box display={"flex"} flexDirection={"row"} mt={4} mb={4}>
                  <Box width={"45%"}>
                    <Typography
                      variant={isMobileOrTablet ? "body2" : "body1"}
                      fontWeight={400}
                      textAlign={"center"}
                      color={theme?.palette?.primary?.dark}
                    >
                      Valor del anticipo
                    </Typography>
                    <Typography
                      fontSize={"18px"}
                      fontWeight={600}
                      textAlign={"center"}
                      mt={1}
                      color={theme?.palette?.primary?.dark}
                    >
                      {formatCurrency(valorAnticipo || 0)}{" "}
                      <Typography variant="body3" fontWeight={500}>
                        {interfaceConfig.cotizador?.current_currency || "MXN"}
                      </Typography>
                    </Typography>
                  </Box>

                  <Box width={"10%"} display={"flex"} justifyContent={"center"} alignItems={"center"}>
                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{ mr: "-2px", border: `1px solid ${theme?.palette?.primary?.dark}` }}
                    />
                  </Box>

                  <Box width={"45%"}>
                    <Typography
                      variant={isMobileOrTablet ? "body2" : "body1"}
                      fontWeight={400}
                      textAlign={"center"}
                      color={theme?.palette?.primary?.dark}
                    >
                      Valor del residual
                    </Typography>

                    <Typography
                      fontSize={"18px"}
                      fontWeight={600}
                      textAlign={"center"}
                      color={theme?.palette?.primary?.dark}
                      mt={1}
                    >
                      {formatCurrency(valorResidual || 0)}{" "}
                      <Typography variant="body3" fontWeight={500}>
                        {interfaceConfig.cotizador?.current_currency || "MXN"}
                      </Typography>
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box mt={4} mb={4} display={"flex"} justifyContent={"center"}>
                  <Box width={isMobileOrTablet ? "100%" : "45%"}>
                    <Typography
                      variant={isMobileOrTablet ? "body2" : "body1"}
                      fontWeight={400}
                      textAlign={"center"}
                      color={theme?.palette?.primary?.dark}
                    >
                      Valor del anticipo
                    </Typography>
                    <Typography
                      fontSize={"18px"}
                      fontWeight={600}
                      textAlign={"center"}
                      mt={1}
                      color={theme?.palette?.primary?.dark}
                    >
                      {formatCurrency(valorAnticipo || 0)}{" "}
                      <Typography variant="body3" fontWeight={500}>
                        {interfaceConfig.cotizador?.current_currency || "MXN"}
                      </Typography>
                    </Typography>
                  </Box>
                </Box>
              )}

              <Typography
                fontWeight={700}
                fontSize={"16px"}
                color={theme?.palette?.primary?.dark}
                textAlign={"center"}
                mt={2}
              >
                Pago mensual estimado*
              </Typography>

              <Typography
                mb={2}
                textAlign={"center"}
                variant="h4"
                fontWeight={theme?.palette?.fontWeights?.semibold}
                color={theme?.palette?.primary?.dark}
              >
                {isNaN(monthlyRate) || !isFinite(monthlyRate) ? "0" : formatCurrency(monthlyRate)}{" "}
                <Typography
                  variant="h5"
                  component={"span"}
                  fontWeight={theme?.palette?.fontWeights?.semibold}
                  color={theme?.palette?.primary?.dark}
                >
                  MXN
                </Typography>
              </Typography>

              <Box
                display="flex"
                justifyContent="center"
                gap={1}
                sx={{
                  cursor: isLoading ? "not-allowed" : "pointer",
                  textDecoration: isLoading ? "none" : "underline",
                  opacity: isLoading ? 0.5 : 1,
                  pointerEvents: isLoading ? "none" : "auto",
                }}
                onClick={() => {
                  if (!isLoading) {
                    genarateQuotationTable();
                  }
                }}
              >
                <SystemUpdateAltIcon sx={{ color: theme?.palette?.primary?.dark }} />
                <Typography fontSize={"14px"} textAlign={"center"} color={theme?.palette?.primary?.dark}>
                  Descarga aquí la tabla de amortización informativa
                </Typography>
              </Box>

              <Typography
                textAlign={"center"}
                fontWeight={theme?.palette?.fontWeights?.light}
                color={theme?.palette?.primary?.dark}
                mt={2}
                mb={2}
              >
                <small>
                  *Esta simulación es para fines demostrativos y está sujeto a aprobación, las condiciones como la tasa,
                  el monto del crédito, o la comisión por apertura, podrían cambiar dependiendo el perfil y la situación
                  actual del solicitante.
                </small>
              </Typography>

              <Box display="flex" justifyContent="center" mb={2}>
                <Button
                  sx={{ background: theme.palette.primary?.dark }}
                  onClick={setConditions}
                  disabled={isLoading}
                  variant="contained"
                >
                  Continuar
                </Button>
              </Box>
            </Box>
          </Container>
        </Grid>
        {!isMobileOrTablet && (
          <Grid item xs={12} md={4}>
            <CustomRightImage />
          </Grid>
        )}
      </Grid>
    </>
  );
};
export default CotizadorArrendaminetoLaniAuto;
